import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import jsPDF from 'jspdf';
import {
  Users,
  Shield,
  Vote,
  Settings,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Crown,
  Scale,
} from 'lucide-react';

interface CustomGovernanceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface GovernanceConfig {
  overrideAuthority: 'community-elect' | 'community' | 'subpanel' | 'hybrid';
  communityOverrideThreshold: number;
  subpanelMajority: 'simple' | 'super';
  financialThreshold: number;
  emergencyBypass: boolean;
  removalProcess: 'petition' | 'vote' | 'both';
  communitySize: number;
}

const defaultConfig: GovernanceConfig = {
  overrideAuthority: 'hybrid',
  communityOverrideThreshold: 60,
  subpanelMajority: 'simple',
  financialThreshold: 15000,
  emergencyBypass: true,
  removalProcess: 'both',
  communitySize: 406,
};

export function CustomGovernanceModal({ isOpen, onClose }: CustomGovernanceModalProps) {
  const [config, setConfig] = useState<GovernanceConfig>(defaultConfig);
  const [activeTab, setActiveTab] = useState('authority');
  const navigate = useNavigate();

  const handleExportConfig = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 20;

    // Header
    doc.setFontSize(20);
    doc.setTextColor(37, 99, 235); // Blue color
    doc.text('Custom Governance Configuration', pageWidth / 2, yPosition, { align: 'center' });

    yPosition += 10;
    doc.setFontSize(12);
    doc.setTextColor(102, 102, 102);
    doc.text(
      `Generated on ${new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })}`,
      pageWidth / 2,
      yPosition,
      { align: 'center' },
    );

    yPosition += 15;
    doc.text(`Community Size: ${config.communitySize} homes`, pageWidth / 2, yPosition, {
      align: 'center',
    });

    yPosition += 20;

    // Draw a line
    doc.setDrawColor(37, 99, 235);
    doc.line(20, yPosition, pageWidth - 20, yPosition);
    yPosition += 15;

    // Governance Model Section
    doc.setFontSize(16);
    doc.setTextColor(30, 64, 175);
    doc.text('Governance Model', 20, yPosition);
    yPosition += 10;

    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(
      `Authority Structure: ${config.overrideAuthority.charAt(0).toUpperCase() + config.overrideAuthority.slice(1).replace('-', ' ')} Model`,
      25,
      yPosition,
    );
    yPosition += 8;

    const description = getGovernanceDescription();
    const descLines = doc.splitTextToSize(description, pageWidth - 50);
    doc.text(descLines, 25, yPosition);
    yPosition += descLines.length * 6 + 10;

    // Decision-Making Thresholds Section
    doc.setFontSize(16);
    doc.setTextColor(30, 64, 175);
    doc.text('Decision-Making Thresholds', 20, yPosition);
    yPosition += 10;

    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(
      `Community Override Threshold: ${config.communityOverrideThreshold}% of residents needed`,
      25,
      yPosition,
    );
    yPosition += 8;
    doc.text(
      `Financial Decision Trigger: Automatic vote above $${config.financialThreshold.toLocaleString()}`,
      25,
      yPosition,
    );
    yPosition += 8;
    doc.text(
      `Subpanel Voting: ${config.subpanelMajority === 'simple' ? 'Simple majority (51%)' : 'Supermajority (67%)'} required`,
      25,
      yPosition,
    );
    yPosition += 15;

    // Governance Processes Section
    doc.setFontSize(16);
    doc.setTextColor(30, 64, 175);
    doc.text('Governance Processes', 20, yPosition);
    yPosition += 10;

    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(`Emergency Bypass: ${config.emergencyBypass ? 'Enabled' : 'Disabled'}`, 25, yPosition);
    yPosition += 8;

    const removalText =
      config.removalProcess === 'petition'
        ? 'Petition process (25% trigger)'
        : config.removalProcess === 'vote'
          ? 'Direct vote (scheduled)'
          : 'Both petition and direct vote options';
    doc.text(`Member Removal: ${removalText}`, 25, yPosition);
    yPosition += 15;

    // Decision Flow Examples
    if (yPosition > pageHeight - 80) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(16);
    doc.setTextColor(30, 64, 175);
    doc.text('Decision Flow Examples', 20, yPosition);
    yPosition += 10;

    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text('Pool Repair ($8,000):', 25, yPosition);
    yPosition += 6;
    const poolFlow =
      config.financialThreshold > 8000
        ? 'Subpanel reviews → Subpanel votes → Community can override'
        : `Automatic community vote (exceeds $${config.financialThreshold.toLocaleString()} threshold)`;
    const poolLines = doc.splitTextToSize(poolFlow, pageWidth - 50);
    doc.text(poolLines, 30, yPosition);
    yPosition += poolLines.length * 6 + 5;

    doc.text('Budget Approval ($200,000):', 25, yPosition);
    yPosition += 6;
    const budgetFlow = `Community vote required → ${config.communityOverrideThreshold}% approval needed`;
    doc.text(budgetFlow, 30, yPosition);
    yPosition += 15;

    // Expected Outcomes Section
    if (yPosition > pageHeight - 60) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(16);
    doc.setTextColor(16, 185, 129);
    doc.text('Expected Outcomes', 20, yPosition);
    yPosition += 10;

    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(
      '• Decision Speed: proposals run on a defined clock, not the meeting calendar',
      25,
      yPosition,
    );
    yPosition += 8;
    doc.text('• Participation: any eligible household can vote from any device', 25, yPosition);
    yPosition += 8;
    doc.text('• Transparency: Complete audit trail of all decisions', 25, yPosition);
    yPosition += 8;
    doc.text('• Flexibility: Settings adjustable anytime', 25, yPosition);
    yPosition += 20;

    // Footer
    doc.setFontSize(10);
    doc.setTextColor(102, 102, 102);
    doc.text(
      'Generated by ToolboxLogic Community Governance Platform',
      pageWidth / 2,
      pageHeight - 20,
      { align: 'center' },
    );
    doc.text(
      'This configuration can be implemented and adjusted based on your community needs',
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' },
    );

    // Save the PDF
    doc.save(`governance-configuration-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const handleScheduleCall = () => {
    // Navigate to schedule demo page
    navigate('/schedule-demo');
  };

  const updateConfig = (updates: Partial<GovernanceConfig>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  };

  const getGovernanceDescription = () => {
    switch (config.overrideAuthority) {
      case 'community-elect':
        return 'Professional management model - Community Elect makes final decisions, community provides input';
      case 'community':
        return 'Direct democracy - Community has final say on all major decisions';
      case 'subpanel':
        return 'Representative democracy - Elected subpanel makes decisions, accountable to community';
      case 'hybrid':
        return 'Balanced approach - Subpanel handles routine matters, community decides major issues';
    }
  };

  const renderDecisionFlow = () => {
    const scenarios = [
      { name: 'Pool Repair ($8,000)', isRoutine: true, cost: 8000 },
      { name: 'Budget Approval ($200,000)', isRoutine: false, cost: 200000 },
      { name: 'Landscaping Contract ($12,000)', isRoutine: true, cost: 12000 },
      { name: 'Policy Change (Pet Rules)', isRoutine: false, cost: 0 },
    ];

    return (
      <div className="space-y-4">
        <h4 className="font-semibold text-lg">How Your Decisions Would Flow:</h4>
        {scenarios.map((scenario, index) => (
          <Card key={index} className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h5 className="font-medium">{scenario.name}</h5>
                <Badge
                  variant={
                    scenario.cost > config.financialThreshold || !scenario.isRoutine
                      ? 'default'
                      : 'secondary'
                  }
                >
                  {scenario.cost > config.financialThreshold || !scenario.isRoutine
                    ? 'Community Vote'
                    : 'Subpanel Decision'}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {scenario.cost > config.financialThreshold || !scenario.isRoutine ? (
                  <>
                    <Users className="w-4 h-4" />
                    <ArrowRight className="w-3 h-3" />
                    <Vote className="w-4 h-4" />
                    <span>
                      Community votes (requires {config.communityOverrideThreshold}% approval)
                    </span>
                  </>
                ) : (
                  <>
                    <Users className="w-4 h-4" />
                    <ArrowRight className="w-3 h-3" />
                    <Shield className="w-4 h-4" />
                    <span>Subpanel decides ({config.subpanelMajority} majority)</span>
                    <ArrowRight className="w-3 h-3" />
                    <AlertCircle className="w-4 h-4" />
                    <span>Community can override</span>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Settings className="w-6 h-6 text-blue-600" />
            Design Your Community's Governance
          </DialogTitle>
          <DialogDescription>
            Configure a governance system that fits your community's culture, size, and preferences.
            See exactly how decisions would flow with your chosen settings.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="authority">Authority</TabsTrigger>
            <TabsTrigger value="thresholds">Thresholds</TabsTrigger>
            <TabsTrigger value="process">Process</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>

          <TabsContent value="authority" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="w-5 h-5" />
                  Who Has Final Authority?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={config.overrideAuthority}
                  onValueChange={(value: GovernanceConfig['overrideAuthority']) =>
                    updateConfig({ overrideAuthority: value })
                  }
                  className="space-y-4"
                >
                  <div className="flex items-start space-x-3 p-4 border rounded-lg">
                    <RadioGroupItem value="community-elect" id="community-elect" className="mt-1" />
                    <div className="space-y-2 flex-1">
                      <Label htmlFor="community-elect" className="text-base font-medium">
                        Community Elect Authority
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Professional manager model. Community Elect makes final decisions but stays
                        responsive to community needs. Best for: Busy communities that want
                        professional management.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 p-4 border rounded-lg">
                    <RadioGroupItem value="community" id="community" className="mt-1" />
                    <div className="space-y-2 flex-1">
                      <Label htmlFor="community" className="text-base font-medium">
                        Community Authority
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Direct democracy. Community votes on all major decisions, subpanel
                        implements. Best for: Engaged communities that want direct control.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 p-4 border rounded-lg">
                    <RadioGroupItem value="subpanel" id="subpanel" className="mt-1" />
                    <div className="space-y-2 flex-1">
                      <Label htmlFor="subpanel" className="text-base font-medium">
                        Subpanel Authority
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Representative democracy. Elected subpanel makes decisions, accountable
                        through elections. Best for: Communities that trust elected representatives.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 p-4 border rounded-lg border-blue-200 bg-blue-50">
                    <RadioGroupItem value="hybrid" id="hybrid" className="mt-1" />
                    <div className="space-y-2 flex-1">
                      <Label htmlFor="hybrid" className="text-base font-medium">
                        Hybrid Model (Recommended)
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Best of both worlds. Subpanel handles routine decisions, community votes on
                        major issues. Best for: Most communities - efficient but democratic.
                      </p>
                      <Badge className="bg-blue-100 text-blue-800">Most Popular</Badge>
                    </div>
                  </div>
                </RadioGroup>

                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Your Selection:</p>
                  <p className="text-sm text-gray-600 mt-1">{getGovernanceDescription()}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="thresholds" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Community Size</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Number of Homes/Units</Label>
                    <Input
                      type="number"
                      value={config.communitySize}
                      onChange={(e) =>
                        updateConfig({ communitySize: parseInt(e.target.value) || 0 })
                      }
                      className="mt-1"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Override Threshold</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>
                      Community Override Percentage: {config.communityOverrideThreshold}%
                    </Label>
                    <Slider
                      value={[config.communityOverrideThreshold]}
                      onValueChange={([value]) =>
                        updateConfig({ communityOverrideThreshold: value })
                      }
                      max={100}
                      min={25}
                      step={5}
                      className="mt-2"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>25% (Easy)</span>
                      <span>75% (Hard)</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Financial Threshold</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Auto-trigger community vote above:</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span>$</span>
                      <Input
                        type="number"
                        value={config.financialThreshold}
                        onChange={(e) =>
                          updateConfig({ financialThreshold: parseInt(e.target.value) || 0 })
                        }
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Expenses above this amount automatically require community approval
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Subpanel Decisions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Majority Required</Label>
                    <RadioGroup
                      value={config.subpanelMajority}
                      onValueChange={(value: 'simple' | 'super') =>
                        updateConfig({ subpanelMajority: value })
                      }
                      className="mt-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="simple" id="simple" />
                        <Label htmlFor="simple">Simple Majority (51%)</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="super" id="super" />
                        <Label htmlFor="super">Supermajority (67%)</Label>
                      </div>
                    </RadioGroup>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="process" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scale className="w-5 h-5" />
                  Process Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="text-base font-medium">Emergency Bypass</Label>
                  <div className="flex items-center space-x-2 mt-2">
                    <input
                      type="checkbox"
                      checked={config.emergencyBypass}
                      onChange={(e) => updateConfig({ emergencyBypass: e.target.checked })}
                      className="rounded"
                    />
                    <Label>Allow emergency decisions to bypass normal voting process</Label>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    For urgent issues like safety hazards or infrastructure failures
                  </p>
                </div>

                <div>
                  <Label className="text-base font-medium">Subpanel Member Removal</Label>
                  <RadioGroup
                    value={config.removalProcess}
                    onValueChange={(value: 'petition' | 'vote' | 'both') =>
                      updateConfig({ removalProcess: value })
                    }
                    className="mt-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="petition" id="petition" />
                      <Label htmlFor="petition">
                        Petition Process (25% of residents can trigger removal)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="vote" id="vote" />
                      <Label htmlFor="vote">Direct Vote (Scheduled community vote)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="both" id="both" />
                      <Label htmlFor="both">Both Options Available</Label>
                    </div>
                  </RadioGroup>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="preview" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Your Custom Governance Model
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold">Configuration Summary:</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Community Size:</span>
                        <span>{config.communitySize} homes</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Authority Model:</span>
                        <span className="capitalize">
                          {config.overrideAuthority.replace('-', ' ')}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Override Threshold:</span>
                        <span>{config.communityOverrideThreshold}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Financial Trigger:</span>
                        <span>${config.financialThreshold.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Subpanel Majority:</span>
                        <span className="capitalize">{config.subpanelMajority}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold">Expected Outcomes:</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Decision speed: 2-3 weeks vs 3-4 months</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Participation: 45-60% vs current 15-20%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Transparency: Full audit trail</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Flexibility: Adjust settings anytime</span>
                      </div>
                    </div>
                  </div>
                </div>

                {renderDecisionFlow()}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex justify-between items-center pt-6 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <div className="flex gap-3">
            {/* <Button variant="outline" onClick={handleExportConfig}>
              Export Configuration
            </Button> */}
            <Button
              className="bg-linear-to-r from-blue-600 to-purple-600"
              onClick={handleScheduleCall}
            >
              Schedule Implementation Call
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
